import React, { useEffect, useMemo, useRef, useState } from "react";
import { intentCreatesRoutes } from "../import/intent";

// What the operator sees while the save job runs: a thin real progress bar, the phase in plain
// words with real counts, and the roster filling in row by row as the server writes it.
// Everything here comes from GET /jobs/:id — nothing is simulated.

const PHASE_COPY = {
  queued: () => "Getting ready",
  matching: () => "Checking who is already registered",
  schools: () => "Setting up the school",
  geocoding: ({ done, total }) => `Placing addresses on the map · ${done}/${total}`,
  saving: ({ done, total }) => `Saving passengers · ${done}/${total}`,
  routing: () => "Building the services",
  done: () => "Done",
  failed: () => "Stopped",
};

const ACTION_LABEL = {
  add: "added",
  update: "updated",
  skip: "kept",
  link: "placed",
};

export const phaseLabel = (progress, { workspaceStep, intent } = {}) => {
  if (workspaceStep === "creating") return "Opening a private workspace for this file";
  if (!progress) return "Saving";
  const copy = PHASE_COPY[progress.phase] || PHASE_COPY.saving;
  if (progress.phase === "routing" && !intentCreatesRoutes(intent)) return "Finishing up";
  return copy(progress);
};

const percent = (progress) => {
  if (!progress || !progress.total) return null;
  const ratio = Math.min(1, Math.max(0, progress.done / progress.total));
  // The rows are the bulk of the work; services (if any) take the last stretch.
  const span = progress.phase === "routing" ? 96 : 92;
  return Math.round(ratio * span);
};

// The sample roster is written in one pass. There is no row-by-row feed to show — that stream
// belongs to a customer's own spreadsheet, which is still saved a few rows at a time.
export const SampleOpening = ({ fileName }) => (
  <section className="start-live start-live--quiet" role="status" aria-live="polite" aria-label="Opening the sample">
    <div className="start-live__head">
      <p className="start-kicker">{fileName || "Sample roster"}</p>
      <h2 className="start-live__phase">Opening the sample operation</h2>
      <div className="start-live__bar is-indeterminate" role="progressbar" aria-valuemin={0} aria-valuemax={100}>
        <span />
      </div>
      <p className="start-live__summary start-muted">85 students, three schools, and their morning and afternoon services.</p>
    </div>
  </section>
);

const ImportLive = ({ progress, workspaceStep, intent, fileName }) => {
  const pct = percent(progress);
  const rows = progress?.rows || [];
  const counts = progress?.counts || {};
  const listRef = useRef(null);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    try {
      const media = window.matchMedia("(prefers-reduced-motion: reduce)");
      setReduced(Boolean(media.matches));
    } catch {
      /* jsdom / old browsers */
    }
  }, []);

  // Keep the newest rows in view as the list grows.
  useEffect(() => {
    const node = listRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [rows.length]);

  const label = phaseLabel(progress, { workspaceStep, intent });
  const failures = counts.geocodeFailures || 0;
  const summary = useMemo(() => {
    const parts = [];
    if (counts.students) parts.push(`${counts.students} ${counts.students === 1 ? "student" : "students"}`);
    if (counts.parents) parts.push(`${counts.parents} ${counts.parents === 1 ? "parent" : "parents"}`);
    if (counts.updated) parts.push(`${counts.updated} refreshed`);
    if (counts.linked) parts.push(`${counts.linked} placed`);
    if (counts.routes) parts.push(`${counts.routes} ${counts.routes === 1 ? "service" : "services"}`);
    return parts.join(" · ");
  }, [counts.students, counts.parents, counts.updated, counts.linked, counts.routes]);

  return (
    <section className="start-live" role="status" aria-live="polite" aria-label="Saving your roster">
      <div className="start-live__head">
        <p className="start-kicker">{fileName ? `Saving ${fileName}` : "Saving"}</p>
        <h2 className="start-live__phase">{label}</h2>
        <div
          className={`start-live__bar${pct == null ? " is-indeterminate" : ""}`}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct == null ? undefined : pct}
        >
          <span style={pct == null ? undefined : { width: `${pct}%` }} />
        </div>
        <p className="start-live__summary start-muted">
          {summary || "Nothing is written until the first row lands below."}
          {failures > 0 && (
            <span className="start-live__warn"> · {failures} {failures === 1 ? "address" : "addresses"} not found on the map</span>
          )}
        </p>
      </div>

      <div className="start-live__list" ref={listRef}>
        {rows.length === 0 ? (
          <p className="start-live__empty start-muted">{workspaceStep === "creating" ? "Your workspace is being opened." : "First rows will appear here in a moment."}</p>
        ) : (
          <table className="start-table start-live__table">
            <tbody>
              {rows.map((row, index) => (
                <tr key={`${row.rowNumber}-${index}`} className={reduced ? "" : "is-new"}>
                  <td className="start-live__index">{index + 1}</td>
                  <td className="start-live__name">{row.name}</td>
                  <td className="start-live__school">{row.school}</td>
                  <td className="start-live__meta">
                    <span className={`start-live__dot${row.geocoded === false ? " is-off" : row.geocoded ? " is-on" : ""}`} aria-hidden="true" />
                    {row.geocoded === false ? "address not found" : ACTION_LABEL[row.action] || "saved"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

export default ImportLive;
